import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { createBusiness, startEnrolation, validateCredentials } from 'lib/api';
import {
  pauseBusinessCompanyRelation,
  resumeBusinessCompanyRelation,
} from 'lib/api/enrollment';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import ErrorHelper from 'lib/designSystem/ErrorHelper';
import { handleError } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { RelationStatus } from 'pages/MyAccounts/types/businessRelations';
import React, { useState } from 'react';

function EditBusiness(props: any) {
  const {
    setLoading,
    dispatch,
    setOpenEditDialog,
    openEditDialog,
    handleInputChange,
    fetchRelations,
    selectedBusiness,
    business,
  } = props;
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [updatePassword, setUpdatePassword] = useState(false);

  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};
    if (!selectedBusiness.identifier) {
      newErrors.identifier = 'El RUT es requerido';
    }
    if (!selectedBusiness.businessName) {
      newErrors.businessName = 'La razón social es requerida';
    }
    if (!business.active && updatePassword && !selectedBusiness.password) {
      newErrors.password = 'La contraseña es requerida';
    }
    return newErrors;
  };

  const handleEditDialog = async () => {
    setLoading(true);
    setErrors({});
    const fieldErrors = validateFields();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setLoading(false);
      return;
    }
    try {
      if (!business.active && updatePassword) {
        await validateCredentials(dispatch, {
          taxPassword: selectedBusiness.password,
          taxIdentifier: selectedBusiness.identifier,
        });
      }
      if (!business.active) {
        if (updatePassword) {
          await startEnrolation(dispatch, {
            taxPassword: selectedBusiness.password,
            taxIdentifier: selectedBusiness.identifier,
            businessId: selectedBusiness.id,
          });
        }
        await createBusiness(dispatch, {
          identifier: selectedBusiness.identifier,
          name: selectedBusiness.businessName,
          taxPassword: updatePassword ? selectedBusiness.password : '',
        });
      }
      await fetchRelations();
      showAlert(
        dispatch,
        'success',
        `Se ha actualizado el cliente. ${!business.active ? 'Estamos ingestando las nuevas facturas. Estas estarán disponibles en algunos minutos' : ''}`,
      );
    } catch (error: any) {
      showAlert(dispatch, 'error', 'Error al actualizar el cliente');
      handleError(error);
    }
    setLoading(false);
    setOpenEditDialog(false);
  };

  const handlePauseBR = async () => {
    try {
      setLoading(true);
      await pauseBusinessCompanyRelation(dispatch, {
        businessIdentifier: selectedBusiness.identifier,
      });
      if (selectedBusiness.status === RelationStatus.ACTIVE) {
        showAlert(
          dispatch,
          'success',
          'El cliente quedó por eliminar, va a tener cancelación efectiva al cierre de ciclo y se eliminará definitivamente. Aún puedes reactivarlo en cualquier momento.',
        );
      } else {
        showAlert(
          dispatch,
          'success',
          'El cliente ha sido eliminado. Puedes reactivarlo en cualquier momento.',
        );
      }

      await fetchRelations();
    } catch (error: any) {
      showAlert(dispatch, 'error', 'Error al desactivar el cliente');
      handleError(error);
    }
    setLoading(false);
    setOpenEditDialog(false);
  };

  const handleResumeBR = async () => {
    try {
      setLoading(true);
      await resumeBusinessCompanyRelation(dispatch, {
        businessIdentifier: selectedBusiness.identifier,
      });
      showAlert(dispatch, 'success', 'El cliente ha sido reactivado.');
      await fetchRelations();
    } catch (error: any) {
      showAlert(dispatch, 'error', 'Error al reactivar el cliente');
      handleError(error);
    }
    setLoading(false);
    setOpenEditDialog(false);
  };

  return (
    <Dialog
      open={openEditDialog}
      onClose={() => setOpenEditDialog(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
        Editar Cliente
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            margin="dense"
            name="identifier"
            disabled
            label="RUT"
            fullWidth
            value={selectedBusiness.identifier}
            onChange={handleInputChange}
            error={!!errors.identifier}
            helperText={<ErrorHelper message={errors.identifier} />}
          />
          <TextField
            margin="dense"
            name="businessName"
            label="Razón Social"
            fullWidth
            value={selectedBusiness.businessName}
            onChange={handleInputChange}
            error={!!errors.businessName}
            helperText={<ErrorHelper message={errors.businessName} />}
          />
          {!business.active && updatePassword && (
            <TextField
              margin="dense"
              name="password"
              label="Contraseña"
              value={selectedBusiness.password}
              onChange={handleInputChange}
              type="password"
              fullWidth
              error={!!errors.password}
              helperText={<ErrorHelper message={errors.password} />}
            />
          )}
          {!business.active && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={updatePassword}
                  onChange={(_, checked) => setUpdatePassword(checked)}
                  color="primary"
                />
              }
              label="Actualizar contraseña"
            />
          )}
        </Box>
        {business.pausedAt && (
          <AlertBanner
            severity="warning"
            title="Cliente desactivado"
            message="El cliente se encuentra desactivado. A final de mes se eliminará definitivamente. Puedes reactivarlo en cualquier momento."
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={() => setOpenEditDialog(false)}
          color="secondary"
        >
          Cancelar
        </CustomButton>
        {!business.pausedAt && (
          <CustomButton onClick={() => handlePauseBR()} color="error">
            Eliminar
          </CustomButton>
        )}
        {business.pausedAt && (
          <CustomButton onClick={() => handleResumeBR()} color="success">
            Reactivar
          </CustomButton>
        )}
        {!business.active && (
          <CustomButton onClick={() => handleEditDialog()} color="primary">
            Actualizar
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default EditBusiness;
